// New Jersey Institute of Technology: public college page (#2652), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - NJIT's Common Data Set 2025-2026 (an Excel file the Office of
//    Institutional Effectiveness links as "2025-26 (xlsx)" from its External
//    Surveys page; the file sits on Google Drive, so the SOURCES url is the
//    njit.edu page that links it). C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table; C8 test policy for Fall 2027;
//    C13/C14 fee and dates; C21/C22 early plans. Its G5 transportation and
//    other expenses are blank, so the cost comes from the admissions page.
//  - njit.edu/admissions: Dates & Deadlines: Undergraduate (the page carries
//    no year; it is the page as posted for fall 2027 applicants), Admission
//    Requirements (how-we-evaluate-applicants: the test-optional policy for
//    Spring 2027 and Fall 2027, superscoring, portfolio), How to Apply
//    (Common App, checklist), and Tuition & Costs (Undergraduate Cost of
//    Attendance for 2026 - 2027).
//  - College Scorecard, school id 185828 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Cost: the admissions page's 2026-2027 figures for a student living on
// campus. Its tuition, fees and food and housing match CDS G1. Books are the
// non-architecture figure ($4,800 for architecture). The rest of NJIT's
// cost of attendance is one unitemized "Additional Indirect Cost" ($5,500 on
// campus); the page says its components include transportation, personal
// expenses and federal loan fees.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'NJIT Common Data Set 2025-2026',
        url: 'https://www.njit.edu/oie/external-surveys',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'NJIT Admissions, Dates & Deadlines: Undergraduate',
        url: 'https://www.njit.edu/admissions/dates-deadlines',
        read: '2026-10-01',
    },
    evaluate: {
        label: 'NJIT Admissions, Admission Requirements',
        url: 'https://www.njit.edu/admissions/how-we-evaluate-applicants',
        read: '2026-10-01',
    },
    howApply: {
        label: 'NJIT Admissions, How to Apply',
        url: 'https://www.njit.edu/admissions/how-apply',
        read: '2026-10-01',
    },
    costs: {
        label: 'NJIT Admissions, Tuition & Costs',
        url: 'https://www.njit.edu/admissions/tuition-costs',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?185828-New-Jersey-Institute-of-Technology',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'new-jersey-institute-of-technology',
    kbId: 'new_jersey_institute_of_technology',
    name: 'New Jersey Institute of Technology',
    shortName: 'NJIT',
    location: 'Newark, NJ',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (NJIT wayfinding and signage guidelines); decoration only.
    branding: {
        primary: '#D22630',
        secondary: '#071D49',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/new_jersey_institute_of_technology_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 18170,
        admitted: 12242,
        enrolled: 1832,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 11765, admitted: 8228 },
            { group: 'Out-of-state', applied: 4533, admitted: 2843 },
            { group: 'International', applied: 1872, admitted: 1171 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'NJIT\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'NJIT has no early decision plan. Its early action plan is nonbinding and not restrictive; the Common Data Set gives a November 15 closing date and a December 15 notification date, but no count of early applicants or admits, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'You apply to NJIT through the Common App with a high school transcript, the Common App essay and a letter of recommendation. Some programs ask for more:',
        rows: [
            { school: 'Albert Dorman Honors College', requirement: 'Test-optional admission does not apply: Honors College applicants must submit SAT or ACT scores.', source: 'evaluate' },
            { school: 'Accelerated programs', requirement: 'Test-optional admission does not apply: applicants must submit SAT or ACT scores.', source: 'evaluate' },
            { school: 'Architecture, Digital Design, Industrial Design and Interior Design', requirement: 'A digital portfolio of creative work is required. NJIT says a strong portfolio may in some instances determine acceptance.', source: 'evaluate' },
        ],
        source: 'howApply',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Rolling admission deadline', value: 'March 1' },
        rows: [
            { plan: 'Early Action I', deadline: 'November 15', decision: null, binding: false },
            { plan: 'Early Action II', deadline: 'December 15', decision: null, binding: false },
            { plan: 'Honors College', deadline: 'February 1', decision: null, binding: false },
            { plan: 'Rolling Admission', deadline: 'March 1', decision: null, binding: false },
        ],
        note: 'NJIT evaluates your application as soon as your file is complete. The non-refundable application fee is $75. The deadlines page does not give decision dates.',
        source: 'deadlines',
        binding: 'Early action is nonbinding, and NJIT\'s plan is not restrictive.',
        bindingSource: 'cds',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'First-year students applying for Fall 2027 can choose not to submit SAT or ACT scores. NJIT strongly encourages them, and some endowed scholarships may require them. Honors College and accelerated program applicants must submit scores. NJIT considers the SAT or ACT and superscores, using your highest scores even if not from your latest sitting.',
        source: 'evaluate',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for some fall 2027 applicants.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'evaluate',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [18352, 38226] },
            { item: 'Fees', amounts: [4074, 4074] },
            { item: 'Housing and food (on campus)', amounts: [18900, 18900] },
            { item: 'Books and supplies', amounts: [2900, 2900] },
            { item: 'Additional indirect cost', amounts: [5500, 5500] },
        ],
        totals: [49726, 69600],
        note: 'For an undergraduate living on campus, two semesters full-time. Books and supplies are $4,800 for architecture students. NJIT does not itemize its additional indirect cost; its cost of attendance also covers transportation, personal expenses and federal loan fees.',
        source: 'costs',
        netPrice: {
            average: 18783,
            byIncome: [
                { income: '$0 to $30,000', amount: 12394 },
                { income: '$30,001 to $48,000', amount: 12805 },
                { income: '$48,001 to $75,000', amount: 17480 },
                { income: '$75,001 to $110,000', amount: 25020 },
                { income: 'Over $110,000', amount: 29832 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from NJIT\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
